/**
 * ONE TAP — procedural decal system.
 *
 * ## The kit is closed, and it is short
 *
 * Eight marks exist. That is the whole vocabulary the building is allowed to paint on
 * itself, and nothing may be added to it that does not answer a wayfinding question:
 *
 * | mark       | the question it answers            |
 * | ---------- | ---------------------------------- |
 * | `label`    | what is this place called?         |
 * | `numeral`  | which storey / which bay is this?  |
 * | `zoneMark` | which storey is this, said at architectural scale? |
 * | `chevron`  | can I run this wall / which way does this ramp fall? |
 * | `arrow`    | which way is the middle of the map? |
 * | `laneDash` | which way does this deck run?       |
 * | `keepClear`| does the ground stop here?          |
 * | `ring`     | where exactly do I stand?           |
 * | `helipad`  | which deck is the big open one?     |
 *
 * `beam` — a vertical shaft of light over the gun pad — has gone with the rest of the
 * arena's glow. It was the last member of the additive batch, and outdoors under a midday
 * sun a light shaft is not a marker, it is haze standing in front of the one square metre
 * a player most needs to read a silhouette against. **The glow batch is now empty**, which
 * is the correct state for this game: the only thing in the world allowed to burn is a
 * chambered gun (DESIGN.md 4). The machinery survives because it costs nothing and the
 * next genuinely-emissive mark should not have to re-invent it.
 *
 * `helipad` is the one addition since the kit closed, and it earns its place on the same
 * test as every other member: it answers a wayfinding question, and it answers one nothing
 * else in the kit can. The arena is a rooftop (DESIGN.md 4), the biggest open deck on a
 * rooftop is a landing pad, and a bordered circle with an H in it is the single most
 * universally-read mark that exists on a roof — legible from the air, from across the map,
 * and at a glance while running. It is also the *only* large mark this file paints on the
 * ground, and it is painted in ink at the kit's one weight, never as a glow.
 *
 * Ten more marks used to live here and every one of them has been deleted, because a
 * decal that answers nothing is indistinguishable from a rendering fault on a white
 * plane. `noticePlate` was a page-of-text glyph that read as clip art at every distance
 * anyone saw it from. `cornerMark` was an L-shaped registration tick which, rotated
 * through its four placements, read as a stray `F`, `L` or `K` floating on a wall.
 * `barcode` was a scribble. `boltPlate`, `seam`, `hazardTri`, `stripe` and `target` were
 * fabrication detail nobody can act on. `footScuff` was random-angle ellipses scattered
 * across every deck — the "unmotivated black diagonal slashes with no source object"
 * that made the spawn floor look like a debug overlay. `glow` was a soft radial blob
 * that could not be told apart from a bloom artefact.
 *
 * What survives is hard-edged, orthogonal and architectural: the kind of paint a
 * facilities department applies with a stencil and a straight edge. **One weight, one
 * value.** Callers tint every non-team mark with `PALETTE.ink` at a single strength, so
 * the signage reads as one system rather than as a pile of separately-authored art.
 *
 * The whole system is one draw call now, and can be two:
 *   - one matte batch  (MeshToonMaterial, lit, alpha-tested) — every mark in the arena
 *   - one glow batch   (additive, unlit) — currently empty; see the note on `beam` above
 *
 * Art is authored as a white/grey alpha mask; per-decal tint comes from a vertex
 * colour attribute, so a single atlas covers every palette colour in the game.
 *
 * Client-only: this file touches the DOM (canvas) and `three`. It must never be
 * imported from src/shared.
 */

import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Atlas layout
// ---------------------------------------------------------------------------

/** One 2048² RGBA atlas ≈ 16 MB resident. Big enough for ~70 unique marks. */
const ATLAS_SIZE = 2048;
/** Transparent gutter between cells so mip levels never bleed one mark into its neighbour. */
const ATLAS_PAD = 6;

/**
 * Alpha-mask tones. There are two, and the second one is a hatch.
 *
 * The mask is tinted per-decal by a vertex colour, so a grey in this canvas is not a
 * darker paint — it is a *fainter* copy of whatever colour the caller chose. That makes
 * every "drop shadow" and "shade" tone a lie at authoring time, and all of them have been
 * removed. `MID` survives only inside `drawKeepClear`, where a lighter hatch inside a solid
 * border is genuinely what the mark looks like.
 */
const FILL = '#ffffff';
const MID = '#b4b4b4';

/**
 * The closed signage kit. Adding a member to this union is an art-direction decision,
 * not a convenience — see the table at the top of this file.
 */
export type DecalKind =
  // Named places
  | 'label'
  | 'numeral'
  | 'zoneMark'
  // Direction
  | 'arrow'
  | 'chevron'
  | 'laneDash'
  // The ground
  | 'keepClear'
  | 'ring'
  | 'helipad';

export interface DecalCell {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  /** width / height of the authored art, so quads can be placed without stretching. */
  aspect: number;
}

export interface DecalAtlas {
  texture: THREE.Texture;
  /** Cells for a kind, in the order they were baked. */
  cells(kind: DecalKind): readonly DecalCell[];
  /** Deterministic pick. `index` selects explicitly; otherwise the rng chooses. */
  pick(kind: DecalKind, rng: () => number, index?: number): DecalCell;
  /** Cell baked for a specific `label` string, or null if that string was not requested. */
  label(text: string): DecalCell | null;
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Placement description
// ---------------------------------------------------------------------------

export interface DecalPlacement {
  cell: DecalCell;
  /** Point on the surface. The quad is pushed out along `normal` automatically. */
  pos: THREE.Vector3;
  /** Outward surface normal (unit). */
  normal: THREE.Vector3;
  /** Width in metres. Height follows from the cell aspect unless `height` is given. */
  width: number;
  height?: number;
  /** Rotation about the normal, radians. */
  rotation?: number;
  /** Tint, multiplied into the mask. Defaults to white. */
  color?: number;
  /**
   * Scales the tint down without needing alpha blending. Every non-team mark in the arena
   * passes `P.SIGNAGE_STRENGTH` here — one weight, one value — so this exists for the
   * handful of callers that genuinely need a different one, not as a per-mark taste dial.
   */
  strength?: number;
  /** Route to the additive/unlit batch instead of the lit one. */
  glow?: boolean;
  /** Extra push along the normal, on top of the default bias. */
  offset?: number;
}

// ---------------------------------------------------------------------------
// Canvas helpers
// ---------------------------------------------------------------------------

function newCanvas(w: number, h: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  if (!g) throw new Error('decals: 2D canvas context unavailable');
  return { c, g };
}

const STENCIL_FONT = `'Arial Black', 'Archivo Black', 'Helvetica Neue', Impact, sans-serif`;

function clampN(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// ---------------------------------------------------------------------------
// Art: the individual marks
// ---------------------------------------------------------------------------

/**
 * Points +X. Bold head, chunky tail, one flat fill.
 *
 * The offset "shadow" this used to carry had the same defect as the one on the stencil
 * text: in an alpha mask a shadow is not dark, it is a dimmer copy of the caller's tint,
 * so it read as a soft double edge on a mark whose whole job is to be crisp.
 */
function drawArrow(g: CanvasRenderingContext2D, w: number, h: number, _rng: () => number): void {
  const tail = h * 0.26;
  g.fillStyle = FILL;
  g.beginPath();
  g.moveTo(w * 0.96, h * 0.5);
  g.lineTo(w * 0.5, h * 0.06);
  g.lineTo(w * 0.5, h * 0.5 - tail * 0.5);
  g.lineTo(w * 0.04, h * 0.5 - tail * 0.5);
  g.lineTo(w * 0.04, h * 0.5 + tail * 0.5);
  g.lineTo(w * 0.5, h * 0.5 + tail * 0.5);
  g.lineTo(w * 0.5, h * 0.94);
  g.closePath();
  g.fill();
}

function drawChevron(g: CanvasRenderingContext2D, w: number, h: number, _rng: () => number): void {
  // Two stacked chevrons pointing +X — the "kinetic" mark used on wall-runnable faces.
  const bar = h * 0.17;
  g.fillStyle = FILL;
  for (let i = 0; i < 2; i++) {
    const x0 = w * (0.06 + i * 0.44);
    g.beginPath();
    g.moveTo(x0, h * 0.08);
    g.lineTo(x0 + w * 0.38, h * 0.5);
    g.lineTo(x0, h * 0.92);
    g.lineTo(x0 + bar, h * 0.92);
    g.lineTo(x0 + w * 0.38 + bar, h * 0.5);
    g.lineTo(x0 + bar, h * 0.08);
    g.closePath();
    g.fill();
  }
}

function drawRing(g: CanvasRenderingContext2D, w: number, h: number, _rng: () => number): void {
  const cx = w * 0.5;
  const cy = h * 0.5;
  g.strokeStyle = FILL;
  g.lineWidth = h * 0.055;
  g.beginPath();
  g.arc(cx, cy, h * 0.42, 0, Math.PI * 2);
  g.stroke();
  g.lineWidth = h * 0.022;
  g.beginPath();
  g.arc(cx, cy, h * 0.3, 0, Math.PI * 2);
  g.stroke();
  // Four break ticks so the ring reads as machined, not drawn.
  g.lineWidth = h * 0.07;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI * 0.25;
    g.beginPath();
    g.moveTo(cx + Math.cos(a) * h * 0.2, cy + Math.sin(a) * h * 0.2);
    g.lineTo(cx + Math.cos(a) * h * 0.46, cy + Math.sin(a) * h * 0.46);
    g.stroke();
  }
}

/**
 * One dash of a painted floor lane line. Slightly rounded ends, because a roller
 * leaves rounded ends; a pure rectangle reads as a UI element rather than as paint.
 */
function drawLaneDash(g: CanvasRenderingContext2D, w: number, h: number, _rng: () => number): void {
  const r = h * 0.5;
  g.fillStyle = FILL;
  g.beginPath();
  g.moveTo(r, 0);
  g.lineTo(w - r, 0);
  g.arc(w - r, r, r, -Math.PI * 0.5, Math.PI * 0.5);
  g.lineTo(r, h);
  g.arc(r, r, r, Math.PI * 0.5, Math.PI * 1.5);
  g.closePath();
  g.fill();
}

/**
 * The painted keep-clear box: a bordered rectangle with diagonal hatching through it.
 * Universal floor language for "do not put anything here" — and, placed at the head of
 * a drop or a ramp mouth, it tells a player the ground is about to stop being ground.
 */
function drawKeepClear(g: CanvasRenderingContext2D, w: number, h: number, _rng: () => number): void {
  const b = Math.min(w, h) * 0.055;
  g.fillStyle = FILL;
  g.fillRect(0, 0, w, b);
  g.fillRect(0, h - b, w, b);
  g.fillRect(0, 0, b, h);
  g.fillRect(w - b, 0, b, h);

  g.save();
  g.beginPath();
  g.rect(b * 2, b * 2, w - b * 4, h - b * 4);
  g.clip();
  g.strokeStyle = MID;
  g.lineWidth = b * 1.1;
  const step = Math.min(w, h) * 0.22;
  for (let x = -h; x < w + h; x += step) {
    g.beginPath();
    g.moveTo(x, 0);
    g.lineTo(x + h, h);
    g.stroke();
  }
  g.restore();
}

/**
 * The helipad mark: a heavy bordered circle with a stencilled H set inside it.
 *
 * Drawn with a straight edge and a compass and nothing else — no shading, no second weight,
 * no ornament. The border ring is what carries at distance (a circle is recognisable long
 * after a letterform has dissolved), and the H is what confirms it up close. The bar
 * proportions are the real ones: the uprights are a fifth of the width, the crossbar a
 * sixth of the height, which is why it reads as a marking rather than as a typed letter.
 */
function drawHelipad(g: CanvasRenderingContext2D, w: number, h: number, _rng: () => number): void {
  const cx = w * 0.5;
  const cy = h * 0.5;
  const r = Math.min(w, h) * 0.46;
  g.strokeStyle = FILL;
  g.lineWidth = Math.min(w, h) * 0.055;
  g.beginPath();
  g.arc(cx, cy, r, 0, Math.PI * 2);
  g.stroke();

  const hw = r * 0.86; // overall width of the H
  const hh = r * 1.04; // overall height
  const stroke = hw * 0.26;
  g.fillStyle = FILL;
  g.fillRect(cx - hw * 0.5, cy - hh * 0.5, stroke, hh);
  g.fillRect(cx + hw * 0.5 - stroke, cy - hh * 0.5, stroke, hh);
  g.fillRect(cx - hw * 0.5, cy - hh * 0.09, hw, hh * 0.18);
}

/**
 * Stencil lettering: solid type with horizontal bridges cut out of it. The bridges are
 * what make a region name read as painted through a cut plate rather than as a UI label
 * floating in the world.
 *
 * **No drop shadow.** There used to be one, offset behind every glyph in `SHADE` — and it
 * could not work, because this is an *alpha mask*: the shadow is not painted dark, it is
 * painted at 29% of whatever colour the caller tints the mark with. On a dark board with a
 * white stencil that produces a dim white ghost trailing every letter, which is the muddy,
 * doubled look the region names had. A stencil is a hard-edged mark or it is nothing.
 *
 * No overspray speckle either. An earlier version dusted ~90 dots around every glyph,
 * which is convincing at the 20 cm a crate stencil covers and looks like a swarm of dirt
 * blobs at the fourteen metres a region name covers. Sharp edges scale; noise does not.
 */
function drawStencilText(
  g: CanvasRenderingContext2D,
  w: number,
  h: number,
  text: string,
  opts: { bridges?: boolean } = {},
): void {
  const bridges = opts.bridges !== false;
  let size = h * 0.82;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.font = `${size}px ${STENCIL_FONT}`;
  const measured = g.measureText(text).width;
  const maxW = w * 0.92;
  if (measured > maxW) {
    size *= maxW / measured;
    g.font = `${size}px ${STENCIL_FONT}`;
  }
  g.fillStyle = FILL;
  g.fillText(text, w * 0.5, h * 0.5);

  if (bridges) {
    g.globalCompositeOperation = 'destination-out';
    const cuts = 2;
    for (let i = 0; i < cuts; i++) {
      const y = h * (0.3 + (i / Math.max(1, cuts - 1)) * 0.4);
      g.fillRect(0, y - h * 0.022, w, h * 0.044);
    }
    g.globalCompositeOperation = 'source-over';
  }
}

/**
 * The numeric zone marker: a storey number set between two heavy rules.
 *
 * A bare numeral painted dead-centre on a large white wall is the one mark in the kit that
 * still read as *accidental* — a single glyph with nothing around it looks like a character
 * left behind rather than like something a facilities team set out. Two full-width rules
 * turn the same information into a piece of designed signage: they give the numeral a
 * baseline and a cap line, they are unmistakably drawn with a straight edge, and at a
 * distance where the digit itself is no longer legible the rule pair still says "there is a
 * sign here", which is most of what a wayfinding mark has to do.
 *
 * One weight, and it is the same weight as everything else in the kit — the rule thickness
 * is tied to the numeral's own stroke, not chosen.
 */
function drawZoneMark(g: CanvasRenderingContext2D, w: number, h: number, text: string): void {
  const rule = h * 0.055;
  const gap = h * 0.07;
  g.fillStyle = FILL;
  g.fillRect(0, 0, w, rule);
  g.fillRect(0, h - rule, w, rule);
  // Short end ticks turning down off each rule: a printed mark has corners.
  const tick = w * 0.06;
  g.fillRect(0, rule, rule, tick);
  g.fillRect(w - rule, rule, rule, tick);
  g.fillRect(0, h - rule - tick, rule, tick);
  g.fillRect(w - rule, h - rule - tick, rule, tick);

  const top = rule + gap;
  const bodyH = h - (rule + gap) * 2;
  g.save();
  g.translate(0, top);
  // Bridges off: the rules already read as a cut plate, and bridging a single digit
  // between two rules just makes it look damaged.
  drawStencilText(g, w, bodyH, text, { bridges: false });
  g.restore();
}

// ---------------------------------------------------------------------------
// Atlas construction
// ---------------------------------------------------------------------------

interface ShelfState {
  x: number;
  y: number;
  rowH: number;
}

function allocCell(s: ShelfState, w: number, h: number): { x: number; y: number } {
  if (s.x + w + ATLAS_PAD > ATLAS_SIZE) {
    s.x = ATLAS_PAD;
    s.y += s.rowH + ATLAS_PAD;
    s.rowH = 0;
  }
  const out = { x: s.x, y: s.y };
  s.x += w + ATLAS_PAD;
  if (h > s.rowH) s.rowH = h;
  return out;
}

/**
 * Bake every decal in the game into one texture.
 *
 * `labels` are extra strings (region names, callouts, spawn tags) that get their
 * own cells — the caller knows the arena's vocabulary, this file does not.
 */
export function buildDecalAtlas(rng: () => number, labels: readonly string[] = []): DecalAtlas {
  const { c, g } = newCanvas(ATLAS_SIZE, ATLAS_SIZE);
  g.clearRect(0, 0, ATLAS_SIZE, ATLAS_SIZE);

  const shelf: ShelfState = { x: ATLAS_PAD, y: ATLAS_PAD, rowH: 0 };
  const cells = new Map<DecalKind, DecalCell[]>();
  const labelCells = new Map<string, DecalCell>();

  const bake = (
    kind: DecalKind,
    w: number,
    h: number,
    draw: (ctx: CanvasRenderingContext2D, cw: number, ch: number, r: () => number) => void,
  ): DecalCell => {
    const at = allocCell(shelf, w, h);
    g.save();
    g.translate(at.x, at.y);
    g.beginPath();
    g.rect(0, 0, w, h);
    g.clip();
    draw(g, w, h, rng);
    g.restore();
    const cell: DecalCell = {
      u0: at.x / ATLAS_SIZE,
      v0: 1 - (at.y + h) / ATLAS_SIZE,
      u1: (at.x + w) / ATLAS_SIZE,
      v1: 1 - at.y / ATLAS_SIZE,
      aspect: w / h,
    };
    let list = cells.get(kind);
    if (!list) {
      list = [];
      cells.set(kind, list);
    }
    list.push(cell);
    return cell;
  };

  // One cell per mark, not two or three. Variants were how the kit stopped reading as a
  // kit: two arrows at slightly different proportions are two arrows, and a player who
  // sees the same mark twice at the same size learns it is a *system*.
  bake('arrow', 256, 160, drawArrow);
  bake('chevron', 192, 128, drawChevron);
  bake('laneDash', 256, 40, drawLaneDash);
  bake('keepClear', 256, 256, drawKeepClear);
  bake('ring', 256, 256, drawRing);
  bake('helipad', 512, 512, drawHelipad);

  // Numerals 0-9 as their own cells, for region markers big enough to read across the map.
  for (let n = 0; n <= 9; n++) {
    bake('numeral', 160, 224, (ctx, cw, ch) => drawStencilText(ctx, cw, ch, String(n)));
  }
  // The same digits again, set as designed zone plates. Walls take these; crates and
  // small plates take the bare numeral, because a rule pair on a 40 cm mark is illegible.
  for (let n = 0; n <= 9; n++) {
    bake('zoneMark', 224, 288, (ctx, cw, ch) => drawZoneMark(ctx, cw, ch, String(n)));
  }

  // Caller-supplied strings.
  const seen = new Set<string>();
  for (const raw of labels) {
    const text = raw.toUpperCase();
    if (seen.has(text)) continue;
    seen.add(text);
    const w = clampN(96 + text.length * 88, 128, 1024);
    const cell = bake('label', w, 176, (ctx, cw, ch) => drawStencilText(ctx, cw, ch, text));
    labelCells.set(text, cell);
  }

  const texture = new THREE.CanvasTexture(c);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = 4;
  texture.needsUpdate = true;

  const empty: DecalCell[] = [];
  return {
    texture,
    cells: (kind) => cells.get(kind) ?? empty,
    pick(kind, r, index) {
      const list = cells.get(kind);
      if (!list || list.length === 0) {
        throw new Error(`decals: no cells baked for kind "${kind}"`);
      }
      const i = index === undefined ? (r() * list.length) | 0 : index % list.length;
      return list[clampN(i, 0, list.length - 1)];
    },
    label: (text) => labelCells.get(text.toUpperCase()) ?? null,
    dispose() {
      texture.dispose();
    },
  };
}

// ---------------------------------------------------------------------------
// Placement / batching
// ---------------------------------------------------------------------------

/** Default push-off so a decal never z-fights the surface it is painted on. */
const DECAL_BIAS = 0.012;

interface DecalBatch {
  pos: number[];
  nrm: number[];
  uv: number[];
  col: number[];
}

const emptyBatch = (): DecalBatch => ({ pos: [], nrm: [], uv: [], col: [] });

const _u = new THREE.Vector3();
const _v = new THREE.Vector3();
const _n = new THREE.Vector3();
const _tmp = new THREE.Vector3();
const _col = new THREE.Color();

/**
 * Accumulates decal quads and merges them into at most two meshes.
 *
 * Quads are unrotated in world space around the surface normal, tinted per-vertex,
 * and share one atlas — so 800 marks cost the same two draw calls as 8 do.
 */
export class DecalBuilder {
  private readonly matte = emptyBatch();
  private readonly glow = emptyBatch();
  private count = 0;

  constructor(private readonly atlas: DecalAtlas) {}

  get placed(): number {
    return this.count;
  }

  place(p: DecalPlacement): void {
    _n.copy(p.normal);
    if (_n.lengthSq() < 1e-8) return;
    _n.normalize();

    // Build a stable tangent frame: (right, up, normal) must be right-handed, i.e.
    // right × up === normal, or the quad winds backwards and gets face-culled.
    if (Math.abs(_n.y) > 0.94) {
      _u.set(1, 0, 0);
    } else {
      _u.set(0, 1, 0);
    }
    _v.crossVectors(_u, _n).normalize(); // right
    _u.crossVectors(_n, _v).normalize(); // up
    // _u is "up" on the surface, _v is "right"; the art's +X runs along _v.

    const rot = p.rotation ?? 0;
    if (rot !== 0) {
      const cs = Math.cos(rot);
      const sn = Math.sin(rot);
      const ux = _u.x * cs + _v.x * sn;
      const uy = _u.y * cs + _v.y * sn;
      const uz = _u.z * cs + _v.z * sn;
      const vx = _v.x * cs - _u.x * sn;
      const vy = _v.y * cs - _u.y * sn;
      const vz = _v.z * cs - _u.z * sn;
      _u.set(ux, uy, uz);
      _v.set(vx, vy, vz);
    }

    const w = p.width;
    const h = p.height ?? p.width / Math.max(0.01, p.cell.aspect);
    const hw = w * 0.5;
    const hh = h * 0.5;
    const bias = DECAL_BIAS + (p.offset ?? 0);

    _tmp.copy(p.pos).addScaledVector(_n, bias);

    const s = p.strength ?? 1;
    _col.setHex(p.color ?? 0xffffff);
    const cr = _col.r * s;
    const cg = _col.g * s;
    const cb = _col.b * s;

    const batch = p.glow ? this.glow : this.matte;
    const c = p.cell;

    // corners: (-u,-v) (+v) (+u) — two triangles, CCW when viewed from +normal.
    const corner = (su: number, sv: number, out: number[]): void => {
      out.push(
        _tmp.x + _v.x * hw * sv + _u.x * hh * su,
        _tmp.y + _v.y * hw * sv + _u.y * hh * su,
        _tmp.z + _v.z * hw * sv + _u.z * hh * su,
      );
    };
    const push = (su: number, sv: number, uu: number, vv: number): void => {
      corner(su, sv, batch.pos);
      batch.nrm.push(_n.x, _n.y, _n.z);
      batch.uv.push(uu, vv);
      batch.col.push(cr, cg, cb);
    };

    push(-1, -1, c.u0, c.v0);
    push(-1, 1, c.u1, c.v0);
    push(1, 1, c.u1, c.v1);

    push(-1, -1, c.u0, c.v0);
    push(1, 1, c.u1, c.v1);
    push(1, -1, c.u0, c.v1);

    this.count++;
  }

  /** Convenience: place by kind, letting the atlas choose a variant. */
  placeKind(kind: DecalKind, rng: () => number, p: Omit<DecalPlacement, 'cell'> & { variant?: number }): void {
    this.place({ ...p, cell: this.atlas.pick(kind, rng, p.variant) });
  }

  private toGeometry(b: DecalBatch): THREE.BufferGeometry | null {
    if (b.pos.length === 0) return null;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(b.pos, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(b.nrm, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(b.uv, 2));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(b.col, 3));
    geo.computeBoundingSphere();
    return geo;
  }

  /**
   * Materialise the batches. `matteMaterial` should be lit (decals sit in world
   * shading); `glowMaterial` should be additive and unlit.
   */
  build(matteMaterial: THREE.Material, glowMaterial: THREE.Material): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    const m = this.toGeometry(this.matte);
    if (m) {
      const mesh = new THREE.Mesh(m, matteMaterial);
      mesh.name = 'decals-matte';
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.renderOrder = 2;
      mesh.matrixAutoUpdate = false;
      out.push(mesh);
    }
    const gl = this.toGeometry(this.glow);
    if (gl) {
      const mesh = new THREE.Mesh(gl, glowMaterial);
      mesh.name = 'decals-glow';
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.renderOrder = 3;
      mesh.matrixAutoUpdate = false;
      out.push(mesh);
    }
    return out;
  }
}

/**
 * Material for the lit decal batch. Shares the arena's toon gradient so painted
 * marks band exactly like the surfaces they sit on.
 */
export function makeDecalMaterial(atlas: DecalAtlas, gradient: THREE.Texture): THREE.Material {
  const m = new THREE.MeshToonMaterial({
    map: atlas.texture,
    gradientMap: gradient,
    color: 0xffffff,
    vertexColors: true,
    transparent: true,
    alphaTest: 0.04,
    depthWrite: false,
    side: THREE.FrontSide,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });
  m.name = 'decal-matte';
  return m;
}

/**
 * Material for the additive decal batch. **Nothing routes through here any more**, and
 * that is deliberate: on a sunlit roof every glowing mark this used to draw was either
 * invisible or in the way, and anything that glows in the arena is competing with the one
 * thing DESIGN.md 4 says must always read — a chambered gun. Kept because the batch costs
 * nothing when empty and the next genuinely-emissive mark should not have to rebuild it.
 */
export function makeDecalGlowMaterial(atlas: DecalAtlas): THREE.Material {
  const m = new THREE.MeshBasicMaterial({
    map: atlas.texture,
    vertexColors: true,
    transparent: true,
    alphaTest: 0.02,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.FrontSide,
    toneMapped: false,
    polygonOffset: true,
    polygonOffsetFactor: -6,
    polygonOffsetUnits: -6,
  });
  m.name = 'decal-glow';
  return m;
}
